import React, { useState } from 'react';
import { 
  Box,
  Button,
  CssBaseline,
  TextField,
  Typography,
  Link,
  CircularProgress,
  styled,
  Paper,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import { FiEye, FiEyeOff } from 'react-icons/fi'; // React Icons for professional look
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { postData } from '../../Webservice/webservice';
import { useNavigate } from 'react-router-dom';

// Custom styled components for a professional UI with single background
const StyledRoot = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundImage: 'url("../img/vlogin.jpeg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(34, 34, 35, 0.45)', // Darker overlay for better contrast
    zIndex: 1,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const StyledFormSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  maxWidth: 480,
  padding: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const StyledForm = styled(Paper)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(5),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: 'rgba(255, 255, 255, 0.95)', // Semi-transparent white for contrast
  boxShadow: theme.shadows[8],
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

// Professional theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#0d47a1', // Deep blue for professionalism
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#f0f4f8',
      paper: 'rgba(255, 255, 255, 0.95)',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    subtitle1: {
      fontWeight: 500,
    },
    body2: {
      fontSize: '0.875rem',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.3s ease',
            '&:hover fieldset': {
              borderColor: '#0d47a1',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#0d47a1',
            },
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#424242',
          '&.Mui-focused': {
            color: '#0d47a1',
          },
        },
      },
    },
  },
});

const VendorLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(null);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [otpSuccess, setOtpSuccess] = useState(null);
  const [forgotError, setForgotError] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(null);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      const response = await postData('vendor/login', { email, password });

      if (response && response.token) {
        console.log('Login successful!');
        
        // Clear any existing common user session data first
        sessionStorage.removeItem('commonToken');
        sessionStorage.removeItem('commonUser');
        sessionStorage.removeItem('commonUserId');
        sessionStorage.removeItem('userType');
        
        // Store vendor data with separate keys
        localStorage.setItem('vendorToken', response.token);
        localStorage.setItem('vendorRole', response.role);
        localStorage.setItem('vendorUser', JSON.stringify(response));
        localStorage.setItem('userType', 'vendor');
        
        if (response.id) {
          localStorage.setItem('vendorUserId', response.id);
        } else if (response._id) {
          localStorage.setItem('vendorUserId', response._id);
        }
        
        // Keep legacy keys for backward compatibility
        sessionStorage.setItem('user', JSON.stringify(response));
        sessionStorage.setItem('token', response.token);
        if (response.id) {
          sessionStorage.setItem('userId', response.id);
        } else if (response._id) {
          sessionStorage.setItem('userId', response._id);
        }
        
        console.log('VendorLoginPage - Stored user data:', response);
        console.log('Vendor token stored:', response.token);
        console.log('Token expiry check - localStorage vendorToken:', localStorage.getItem('vendorToken'));
        console.log('Redirecting to /');
        navigate('/');
      } else {
        setError(response?.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handler for sending OTP (simulate API call)
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setForgotError(null);
    setForgotSuccess(null);
    setShowOtpInput(false);
    if (!forgotEmail) {
      setForgotError('Please enter your email address.');
      return;
    }
    try {
      setForgotLoading(true);
      const res = await postData('vendor/forgot-password', { email: forgotEmail });
      if (res && (res.success || res.message === 'OTP sent to email')) {
        setForgotSuccess(res.message || 'OTP sent to your email address!');
        setShowOtpInput(true);
      } else {
        setForgotError(res?.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setForgotError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Handler for verifying OTP (simulate API call)
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setOtpError(null);
    setOtpSuccess(null);
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter the 6-digit OTP.');
      return;
    }
    try {
      setOtpLoading(true);
      const res = await postData('vendor/otp-verify', { email: forgotEmail, otp });
      if (res && (res.success || res.message === 'OTP verified successfully')) {
        setOtpSuccess(res.message || 'OTP verified successfully!');
        setShowResetPassword(true);
      } else {
        setOtpError(res?.message || 'Failed to verify OTP. Please try again.');
      }
    } catch (err) {
      setOtpError('Failed to verify OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Handler for resetting password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError(null);
    setResetSuccess(null);
    if (!resetPassword || !resetConfirmPassword) {
      setResetError('Please fill in all fields.');
      return;
    }
    if (resetPassword !== resetConfirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }
    if (resetPassword.length < 6) {
      setResetError('Password must be at least 6 characters.');
      return;
    }
    try {
      setResetLoading(true);
      // Simulate API call, replace with your actual API endpoint
      const res = await postData('vendor/reset-password', { email: forgotEmail, password: resetPassword });
      await new Promise((resolve) => setTimeout(resolve, 1200));
      setResetSuccess('Password reset successfully! You can now log in.');
    } catch (err) {
      setResetError('Failed to reset password. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <StyledRoot>
        <StyledFormSection>
          <StyledForm elevation={0}>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography component="h1" variant="h4" sx={{fontFamily: 'Mulish, sans-serif'}}>
                Vendor Portal Login
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 , fontFamily: 'Mulish, sans-serif'}}>
                Secure access to your management dashboard
              </Typography>
            </Box>
            {!showForgot ? (
              <Box component="form" onSubmit={handleLogin} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={Boolean(error && !email)}
                  variant="outlined"
                  size="medium"
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={Boolean(error && !password)}
                  variant="outlined"
                  size="medium"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((show) => !show)}
                          edge="end"
                        >
                          {showPassword ? <FiEye /> : <FiEyeOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                  <Link
                    href="#"
                    variant="body2"
                    sx={{ textDecoration: 'none', color: 'primary.main', fontWeight: 500, fontFamily: 'Mulish, sans-serif', cursor: 'pointer' }}
                    onClick={() => {
                      setShowForgot(true);
                      setForgotEmail('');
                      setForgotError(null);
                      setForgotSuccess(null);
                    }}
                  >
                    Forgot password?
                  </Link>
                </Box>
                {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, py: 1.5 , color: 'white',fontFamily: 'Mulish, sans-serif',
                    backgroundColor: '#2B333A',
                  '&:hover': {
                      backgroundColor: '#2B333A'
                    },}}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Secure Sign In'
                  )}
                </Button>
              </Box>
            ) : (
              <>
                {!showOtpInput ? (
                  <Box component="form" onSubmit={handleSendOTP} noValidate>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontFamily: 'Mulish, sans-serif' }}>
                      Forgot Password
                    </Typography>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="forgot-email"
                      label="Enter your email address"
                      name="forgot-email"
                      autoComplete="email"
                      autoFocus
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      error={Boolean(forgotError && !forgotEmail)}
                      variant="outlined"
                      size="medium"
                    />
                    {forgotError && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{forgotError}</Alert>}
                    {forgotSuccess && <Alert severity="success" sx={{ mt: 2, mb: 2 }}>{forgotSuccess}</Alert>}
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{ mt: 3, mb: 2, py: 1.5 , color: 'white',fontFamily: 'Mulish, sans-serif',
                        backgroundColor: '#2B333A',
                      '&:hover': {
                          backgroundColor: '#2B333A'
                        },}}
                      disabled={forgotLoading}
                    >
                      {forgotLoading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Send OTP'
                      )}
                    </Button>
                    <Button
                      fullWidth
                      variant="text"
                      sx={{ mb: 1, fontFamily: 'Mulish, sans-serif', color: 'primary.main', fontWeight: 500 }}
                      onClick={() => setShowForgot(false)}
                    >
                      Back to Login
                    </Button>
                  </Box>
                ) : (
                  !showResetPassword ? (
                    <Box component="form" onSubmit={handleVerifyOTP} noValidate>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontFamily: 'Mulish, sans-serif' }}>
                        Enter OTP
                      </Typography>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="otp"
                        label="Enter 6-digit OTP"
                        name="otp"
                        autoFocus
                        value={otp}
                        onChange={(e) => {
                          // Only allow numbers and max 6 digits
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setOtp(val);
                        }}
                        error={Boolean(otpError && (!otp || otp.length !== 6))}
                        variant="outlined"
                        size="medium"
                        inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                      />
                      {otpError && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{otpError}</Alert>}
                      {otpSuccess && <Alert severity="success" sx={{ mt: 2, mb: 2 }}>{otpSuccess}</Alert>}
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2, py: 1.5 , color: 'white',fontFamily: 'Mulish, sans-serif',
                          backgroundColor: '#2B333A',
                        '&:hover': {
                            backgroundColor: '#2B333A'
                          },}}
                        disabled={otpLoading}
                      >
                        {otpLoading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          'Verify OTP'
                        )}
                      </Button>
                      <Button
                        fullWidth
                        variant="text"
                        sx={{ mb: 1, fontFamily: 'Mulish, sans-serif', color: 'primary.main', fontWeight: 500 }}
                        onClick={() => {
                          setShowOtpInput(false);
                          setOtp('');
                          setOtpError(null);
                          setOtpSuccess(null);
                        }}
                      >
                        Back to Email
                      </Button>
                    </Box>
                  ) : (
                    <Box component="form" onSubmit={handleResetPassword} noValidate>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontFamily: 'Mulish, sans-serif' }}>
                        Reset Password
                      </Typography>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="reset-email"
                        label="Email Address"
                        name="reset-email"
                        value={forgotEmail}
                        disabled
                        variant="outlined"
                        size="medium"
                      />
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="reset-password"
                        label="New Password"
                        name="reset-password"
                        type="password"
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                        variant="outlined"
                        size="medium"
                      />
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="reset-confirm-password"
                        label="Confirm New Password"
                        name="reset-confirm-password"
                        type="password"
                        value={resetConfirmPassword}
                        onChange={(e) => setResetConfirmPassword(e.target.value)}
                        variant="outlined"
                        size="medium"
                      />
                      {resetError && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{resetError}</Alert>}
                      {resetSuccess && <Alert severity="success" sx={{ mt: 2, mb: 2 }}>{resetSuccess}</Alert>}
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2, py: 1.5 , color: 'white',fontFamily: 'Mulish, sans-serif',
                          backgroundColor: '#2B333A',
                        '&:hover': {
                            backgroundColor: '#2B333A'
                          },}}
                        disabled={resetLoading}
                      >
                        {resetLoading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          'Confirm'
                        )}
                      </Button>
                    </Box>
                  )
                )}
              </>
            )}
          </StyledForm>
        </StyledFormSection>
      </StyledRoot>
    </ThemeProvider>
  );
};

export default VendorLoginPage;